<template>
  <div>
    <h2 class="text-large my-4">
      Welcome {{ user.firstName }}. You have been invited to the following organizations on North.
    </h2>
    <ul class="invitations-list list-unstyled mt-4">
      <li class="py-2" v-for="invitation in invitations" :key="invitation.key">
        <div class="d-flex">
          <div class="flex-grow-1 text-truncate">
            <div class="text-normal-1 bold">{{ invitation.organization.name }}</div>
            <div class="text-secondary text-truncate text-medium-1">
              Invited by
              <span :title="invitation.inviter.email">{{ invitation.inviter.email }}</span>
            </div>
          </div>
          <b-button
            @click="acceptInvitation(invitation)"
            class="join-button text-medium-1"
            :variant="isAccepted(invitation) ? 'outline-primary' : 'primary'"
            :disabled="isAccepted(invitation) || acceptingId === invitation.key"
            pill
          >
            <b-spinner variant="secondary" small v-if="acceptingId === invitation.key"></b-spinner>
            <template v-else>
              {{ isAccepted(invitation) ? "You're in" : 'Join' }}
            </template>
          </b-button>
        </div>
      </li>
    </ul>
    <div class="text-center footer mt-5" v-if="showSubmitButton">
      <b-button @click="navigateToApp" class="px-4" variant="primary" pill
        >Done, let's continue...
      </b-button>
    </div>
  </div>
</template>

<script>
import { EVENT_ACTION } from '@/constants/analytics'
import { ROUTE_NAMES } from '@/constants/routes'
import OnboardingTrackingMixin from '@/mixins/OnboardingTrackingMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import safeJSONParse from '@/utils/safeJSONParse'
import showAPIError from '@/utils/showAPIError'
import startViewTransition from '@/utils/startViewTransition'
import { BButton, BSpinner } from 'bootstrap-vue'

/**
 * It show the list of orgs user has been invited to. It also give an option to accept the invitation
 * props:
 * - invitation
 */
export default {
  name: 'Invitations',
  props: ['invitations'],
  mixins: [OnboardingTrackingMixin],
  components: {
    BButton,
    BSpinner
  },
  data() {
    return {
      acceptedInvitations: [],
      acceptingId: null
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    showSubmitButton() {
      return this.acceptedInvitations.length > 0
    },
    nextRouteName() {
      return this.$route.query.nextRoute || ROUTE_NAMES.ORG_MAIN_HOME
    },
    nextRouteQuery() {
      return safeJSONParse(decodeURIComponent(this.$route.query.nextRouteQuery))
    }
  },
  methods: {
    async acceptInvitation({ key }) {
      try {
        this.acceptingId = key
        await service.client.post(urlManager.invitationDetails(key), {
          action: 'accepted'
        })
        this.acceptedInvitations.push(key)
      } catch (e) {
        showAPIError(e, undefined, 'Error while accepting invitation. Please try again.')
      }
      this.acceptingId = null
    },
    isAccepted({ key }) {
      return this.acceptedInvitations.includes(key)
    },
    async navigateToApp() {
      const acceptedInvitation = this.invitations.find(this.isAccepted)
      const joinedOrgId = acceptedInvitation.organizationId
      const joinedSubOrgId = acceptedInvitation.teamId
      const redirect = this.$route.query.redirect
      if (redirect) {
        window.location.href = redirect
        return
      }
      let location = {
        name: this.nextRouteName,
        params: { orgId: joinedOrgId, subOrgId: joinedSubOrgId },
        query: {
          ...this.nextRouteQuery,
          teamId: joinedSubOrgId
        }
      }
      await this.$store.dispatch('getCurrentUser')
      if (this.isSignUpInProgress()) {
        this.trackOnboardingAnalyticsEvent(EVENT_ACTION.ONBOARDING_COMPLETED)
      }
      startViewTransition(() => {
        this.$router.push(location)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.join-button {
  align-self: center;
  width: 118px;
  padding: 2px 12px;
}

.invitations-list {
  min-height: 150px;
}
.footer {
  position: sticky;
  bottom: 10px;
}
</style>
